import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, User, Save, Shield, Link2, Copy, Bell } from "lucide-react";
import { Switch } from "../components/ui/switch";
import api, { formatApiErrorDetail } from "../lib/api";
import { enablePush, disablePush, pushSupported, getPushStatus, sendTestPush } from "../lib/push";
import { useAuth } from "../context/AuthContext";

export default function Profile() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [age, setAge] = useState(user?.age ?? "");
  const [weight, setWeight] = useState(user?.weight ?? "");
  const [height, setHeight] = useState(user?.height ?? "");
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    (async () => {
      if (!supported) return;
      setPushOn(await getPushStatus());
    })();
  }, [supported]);

  const togglePush = async (val) => {
    setPushBusy(true);
    try {
      if (val) {
        await enablePush();
        setPushOn(true);
        toast.success("Benachrichtigungen aktiviert 🔔");
      } else {
        await disablePush();
        setPushOn(false);
        toast.success("Benachrichtigungen deaktiviert");
      }
    } catch (err) {
      toast.error(err.message || "Fehlgeschlagen");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Test-Benachrichtigung gesendet");
    } catch {
      toast.error("Test fehlgeschlagen");
    }
  };

  const shareUrl = `${window.location.origin}/u/${user?.share_code}`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link kopiert – teile ihn mit einem Freund!");
    } catch {
      toast.error("Kopieren nicht möglich");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name,
        age: age === "" ? null : Number(age),
        weight: weight === "" ? null : Number(weight),
        height: height === "" ? null : Number(height),
      };
      const { data } = await api.put("/profile", payload);
      updateUser(data.user);
      toast.success("Profil gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Profil</h1>

      <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#CCFF00] flex items-center justify-center shrink-0">
          <User className="w-8 h-8 text-black" />
        </div>
        <div className="min-w-0">
          <div className="font-display font-black text-xl tracking-tighter truncate">{user?.name}</div>
          <div className="text-sm text-neutral-500 truncate">{user?.email}</div>
          {user?.role === "admin" && (
            <span className="inline-flex items-center gap-1 mt-1 text-[#CCFF00] text-xs font-bold">
              <Shield className="w-3 h-3" /> Administrator
            </span>
          )}
        </div>
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-4" data-testid="notifications-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#CCFF00]" />
            <div>
              <div className="font-bold text-sm">Push-Benachrichtigungen</div>
              <div className="text-xs text-neutral-500">Anfragen, Cheers, Challenges & Streak</div>
            </div>
          </div>
          {supported ? (
            <Switch data-testid="push-switch" checked={pushOn} disabled={pushBusy} onCheckedChange={togglePush} />
          ) : (
            <span className="text-xs text-neutral-500">n/v</span>
          )}
        </div>
        {!supported && (
          <p className="text-xs text-neutral-500 mt-3">
            Dein Browser unterstützt keine Push-Nachrichten. Auf dem iPhone: App über "Zum Home-Bildschirm" installieren (iOS 16.4+).
          </p>
        )}
        {pushOn && (
          <button onClick={testPush} data-testid="push-test" className="mt-3 text-[#CCFF00] text-xs font-bold">
            Test-Benachrichtigung senden →
          </button>
        )}
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-4" data-testid="share-card">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#CCFF00]" />
          <span className="data-label">Dein Freundschafts-Link</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-3 text-sm text-neutral-300 truncate" data-testid="share-url">
            {shareUrl}
          </div>
          <button onClick={copyShare} data-testid="copy-share" className="w-12 h-12 rounded-xl bg-[#CCFF00] flex items-center justify-center shrink-0 active:scale-95 transition-transform">
            <Copy className="w-5 h-5 text-black" />
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-2">Wer den Link öffnet, sieht dein Profil und kann dir eine Anfrage senden.</p>
      </div>

      {user?.role === "admin" && (
        <button
          data-testid="open-admin"
          onClick={() => navigate("/admin")}
          className="w-full mb-4 bg-[#121212] border border-[#CCFF00]/30 rounded-2xl p-4 flex items-center justify-between active:scale-[0.99] transition-transform"
        >
          <span className="flex items-center gap-2 font-bold">
            <Shield className="w-5 h-5 text-[#CCFF00]" /> Admin-Bereich
          </span>
          <span className="text-neutral-500">›</span>
        </button>
      )}

      <form onSubmit={save} className="space-y-4" data-testid="profile-form">
        <div>
          <label className="data-label block mb-2">Name</label>
          <input
            data-testid="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white focus:border-[#CCFF00] focus:outline-none transition-colors"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="data-label block mb-2">Alter</label>
            <input
              data-testid="profile-age"
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="Jahre"
              className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="data-label block mb-2">Gewicht (kg)</label>
            <input
              data-testid="profile-weight"
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="kg"
              className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="data-label block mb-2">Größe (cm)</label>
          <input
            data-testid="profile-height"
            type="number"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            placeholder="cm"
            className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
          />
        </div>
        <p className="text-xs text-neutral-500">Alter, Gewicht & Größe werden für die Kalorienberechnung genutzt.</p>

        <button
          data-testid="profile-save"
          type="submit"
          disabled={saving}
          className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <Save className="w-5 h-5" /> {saving ? "Speichern..." : "Speichern"}
        </button>
      </form>

      <button
        data-testid="logout-button"
        onClick={logout}
        className="w-full mt-4 bg-[#1A1A1A] border border-white/10 text-[#FF3366] font-bold rounded-full py-4 flex items-center justify-center gap-2 active:scale-95 transition-transform"
      >
        <LogOut className="w-5 h-5" /> Abmelden
      </button>
    </div>
  );
}
